import Echo from 'laravel-echo';
import axios from 'axios';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { router, usePage } from '@inertiajs/vue3';
import { BaseClassroomPageProps } from '@/app/types';
import { useClassroomStoreWithOut } from '@/app/stores/classroom';
import { getUser, isScreen, isStudent, isTeacher } from '@/app/helpers/userHelper';
import { saveNavigationTimestamp } from '@/app/helpers/navigationHelper';
import { BaseModuleState } from './modules/BaseModuleService';
import { Student } from '@/app/api/models/student';
import { Teacher } from '@/app/api/models/teacher';
import { PusherPresenceChannel } from 'laravel-echo/dist/channel';
import { axiosQueue } from '@/app/utils/axiosQueue';
import { cancelPendingRequests } from '@/app/utils/axios';

const pageData = usePage<BaseClassroomPageProps>();
const classroomStore = useClassroomStoreWithOut();

class PusherService {
    private static instance: PusherService | null = null;

    public echo: Echo<'pusher'>;

    public channel: PusherPresenceChannel | null = null;

    public privateChannel: PusherPresenceChannel | null = null;

    private disconnectedStudents: {
        student_id: number;
        disconnected_at: string;
    }[] = []; // 접속이 끊긴 학생 목록

    constructor() {
        this.echo = this.initEcho();
    }

    public static getInstance(): PusherService {
        if (this.instance === null) {
            this.instance = new PusherService();
        }
        return this.instance;
    }

    /**
     * Echo 인스턴스 초기화
     * @returns Echo 인스턴스
     */
    private initEcho() {
        return new Echo({
            broadcaster: 'pusher',
            cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
            encrypted: true,
            key: import.meta.env.VITE_PUSHER_APP_KEY,
            authorizer(channel) {
                return {
                    async authorize(socketId, callback) {
                        try {
                            const guard = pageData.props.auth?.guard || 'student';

                            // guard별로 다른 엔드포인트 사용
                            const authUrl =
                                guard === 'teacher' ? '/broadcasting/teacher/auth' : '/broadcasting/student/auth';

                            const { data } = await axios.post(authUrl, {
                                socket_id: socketId,
                                channel_name: channel.name,
                            });
                            callback(false, data);
                        } catch (err) {
                            callback(true, err);
                        }
                    },
                };
            },
        });
    }

    /**
     * 접속이 끊긴 학생을 체크하는 함수(3초 이상 접속이 끊긴 학생을 제거)(재귀 호출)
     */
    public checkDisconnectedStudent() {
        PusherService.getInstance().disconnectedStudents.forEach((student) => {
            if (dayjs().diff(dayjs(student.disconnected_at), 'seconds') > 3) {
                classroomStore.removeStudent(student.student_id);
                PusherService.getInstance().disconnectedStudents =
                    PusherService.getInstance().disconnectedStudents.filter((s) => s.student_id !== student.student_id);
            }
        });

        requestAnimationFrame(PusherService.getInstance().checkDisconnectedStudent);
    }

    /**
     * 체험 교실의 AI 학생을 온라인 상태처럼 표시
     *
     * 체험 교실(classroom.is_demo === true)인 경우,
     * classroom.students에서 skill_level이 null이 아닌 학생들(AI 학생)을 찾아서
     * 온라인 상태처럼 표시합니다.
     */
    private addDemoStudents() {
        const classroom = pageData.props.classroom;

        // 체험 교실이 아니면 처리하지 않음
        if (!classroom?.is_demo) {
            return;
        }

        const classroomStudents = classroom.students ?? [];

        // AI 학생들만 필터링 (skill_level이 null이 아닌 학생들)
        const aiStudents = classroomStudents.filter((student) => student.skill_level != null);

        if (!aiStudents.length) {
            return;
        }

        aiStudents.forEach((student) => {
            // 이미 추가된 학생이 아니면 추가
            if (isNil(classroomStore.getStudents.find((s) => s.id === student.id))) {
                classroomStore.addStudent(student);
            }
            // 세션 학생으로 등록 (온라인 상태처럼 표시)
            classroomStore.setSessionStudent(student.id, student.name);
        });
    }

    /**
     * 학급 채널 구독
     * @param channel_key
     * @param private_channel_key
     */
    public subscribeClassChannel(channel_key: string, private_channel_key: string) {
        if (this.channel) {
            return;
        }

        this.unsubscribeClassChannel();
        this.checkDisconnectedStudent();
        this.echo = this.initEcho();
        this.channel = this.echo.join(channel_key);

        // 사용자 목록 조회
        this.channel.here(
            (
                users: {
                    guard: 'teacher' | 'student';
                    user: Teacher | Student;
                }[],
            ) => {
                users.forEach((user) => {
                    if (user.guard === 'student') {
                        if (isNil(classroomStore.getStudents.find((student) => student.id === user.user.id))) {
                            let addUser = user.user as Student;

                            // pusher에 있는 학생 정보는 옛날 정보 일 수 있어서 최신 정보가 있다면 그 정보로 갱신
                            const studentInfo = pageData.props.students_info?.find(
                                (student) => student.id === user.user.id,
                            );
                            if (studentInfo) {
                                addUser = {
                                    ...addUser,
                                    ...studentInfo,
                                };
                            }

                            classroomStore.addStudent(addUser);
                            classroomStore.setSessionStudent(addUser.id, addUser.name);
                        }
                    } else {
                        classroomStore.setTeacher(user.user as Teacher);
                    }
                });

                this.addDemoStudents();
            },
        );

        // 사용자 참여 시 처리
        this.channel.joining((user: { guard: 'teacher' | 'student'; user: { id: number; name: string } }) => {
            if (user.guard === 'student') {
                if (isNil(classroomStore.getStudents.find((student) => student.id === user.user.id))) {
                    classroomStore.addStudent(user.user as Student);
                }
                classroomStore.setSessionStudent(user.user.id, user.user.name);

                // 접속이 끊긴 학생 목록에서 제거
                if (this.disconnectedStudents.find((student) => student.student_id === user.user.id)) {
                    this.disconnectedStudents = this.disconnectedStudents.filter(
                        (student) => student.student_id !== user.user.id,
                    );
                }
            } else {
                classroomStore.setTeacher(user.user as Teacher);
            }
        });

        // 사용자 퇴장 시 처리
        this.channel.leaving((user: { guard: 'teacher' | 'student'; user: { id: number; name: string } }) => {
            if (user.guard === 'student') {
                this.disconnectedStudents.push({
                    student_id: user.user.id,
                    disconnected_at: dayjs().toISOString(),
                });
            } else {
                classroomStore.setTeacher(null);
            }
        });

        this.channel.listen('SessionStateUpdatedEvent', (data: { state: BaseModuleState }) => {
            classroomStore.setModuleState(data.state);
        });

        // 통합 캐시 이벤트  처리
        this.channel.listen('ClassroomCacheUpdatedEvent', (data: { changed_field: string; cache_data?: any }) => {
            if (data.changed_field === 'app:focus_screen') {
                // cache_data 자체가 focus_screen의 값 (true/false)
                const focusScreen = Boolean(data.cache_data);
                classroomStore.setIsFocusScreen(focusScreen);
            }

            if (data.changed_field === 'app:navigation' && data.cache_data) {
                const navigationData = data.cache_data;

                if (isStudent()) {
                    this.clearAxiosQueue();

                    router.get(
                        navigationData.url,
                        {},
                        {
                            replace: true,
                            onFinish: () => {
                                this.resetPointerEvents();
                                const user = getUser();
                                if (user) {
                                    saveNavigationTimestamp(user.id, navigationData.timestamp);
                                }
                            },
                        },
                    );
                } else if (isScreen()) {
                    const classroomId = classroomStore.getClassroom?.id;
                    const screenUrl = navigationData.url.replace(/\/app\/?/, `/app/screen/classrooms/${classroomId}/`);
                    router.get(screenUrl, {}, { replace: true });
                }
            }

            if (data.changed_field === 'avatar_equipped' && data.cache_data) {
                const studentId = data.cache_data.student_id;
                const avatarCode = data.cache_data.avatar_code;

                const student = pageData.props.students_info?.find((s) => s.id === studentId);
                if (student) {
                    student.current_avatar_code = avatarCode;
                }
            }
        });

        if (isStudent()) {
            // 새로고침 이벤트
            window.addEventListener('beforeunload', () => {
                this.unsubscribeClassChannel();
            });
        }

        this.subscribePrivateChannel(private_channel_key);
    }

    private clearAxiosQueue() {
        axiosQueue.clearQueue();
        cancelPendingRequests();
        document.body.style.pointerEvents = 'none';
    }

    private resetPointerEvents() {
        document.body.style.pointerEvents = 'auto';
    }

    /**
     * 개인 채널 구독
     * @param channel_key
     * @returns
     */
    public subscribePrivateChannel(channel_key: string) {
        this.privateChannel = this.echo.join(channel_key);

        if (isTeacher()) {
            this.privateChannel.listen('SessionModuleResultUpdatedEvent', (data: { session_module_id: number }) => {
                classroomStore.setIsSessionResultUpdate(true);
                classroomStore.setPendingResultRequestsBySessionModuleId(data.session_module_id);
            });
        }

        if (isStudent()) {
            // 칭찬별 지급시 레이어 표시 (학생만)
            this.privateChannel.listen('SessionStarUpdatedEvent', () => {
                classroomStore.setIsShowStarLayer(true);
                classroomStore.setStarsCount(classroomStore.getStarsCount + 1);
            });
        }
    }

    /**
     * 학급 채널 구독 취소
     */
    public unsubscribeClassChannel() {
        this.echo.disconnect();
        this.channel = null;
        this.privateChannel = null;
        classroomStore.setTeacher(null);
        classroomStore.setStudents([]);
    }
}

export default PusherService;
